import React from 'react';
import { NavBar, Footer } from '../../components/redesign/shared.jsx';
import SEO from '../../components/SEO.jsx';

const GITHUB_ORG = 'https://github.com/Aakeeo';

const CRM_PROJECTS = [
  {
    name: 'aakeeo_suitecrm8_dockerized',
    repoPath: 'aakeeo_suitecrm8_dockerized',
    tag: 'Production Docker environment',
    desc: 'A production-grade Dockerised environment for SuiteCRM 8. PHP-FPM, Nginx, MariaDB, Elasticsearch, cron worker — the same stack we deploy to every client.',
    stack: ['Docker', 'PHP 8.2', 'Nginx', 'Elasticsearch'],
    license: 'AGPL-3.0',
    status: 'maintained',
    primary: true,
  },
  {
    name: 'suitecrmmcp',
    repoPath: 'suitecrmmcp',
    tag: 'MCP server',
    desc: 'A Model Context Protocol server that exposes SuiteCRM to AI agents — Claude, Cursor, any MCP client — with role-aware tool permissions.',
    stack: ['TypeScript', 'MCP', 'SuiteCRM API'],
    license: 'AGPL-3.0',
    status: 'maintained',
    primary: true,
  },
];

const LABS_PROJECTS = [
  {
    name: 'tunnelr',
    repoPath: 'tunnelr',
    tag: 'Labs',
    desc: 'A self-hosted alternative to ngrok. Expose local services to the internet through your own infrastructure, with zero-trust auth baked in.',
    stack: ['Go', 'WireGuard'],
    license: 'MIT',
    status: 'maintained',
    primary: true,
  },
  {
    name: 'llm-wiki-crm',
    repoPath: 'llm-wiki-crm',
    tag: 'Labs',
    desc: 'An experimental wiki that stays current automatically by treating an LLM as the primary editor and a CRM as its source of truth. Built to test an idea about institutional memory.',
    stack: ['Python', 'PostgreSQL', 'LLMs'],
    license: 'MIT',
    status: 'experimental',
  },
];

const CONTRIB_STATS = [
  { k: 'projects maintained', v: '4' },
  { k: 'licenses in play', v: 'MIT · AGPL' },
  { k: 'clients on our Docker stack', v: '40+' },
  { k: 'years of FOSS work', v: '12' },
];

function ProjectCard({ name, repoPath, tag, desc, stack, license, status, primary }) {
  const href = `${GITHUB_ORG}/${repoPath}`;
  return (
    <article className={'osA-card' + (primary ? ' osA-card--primary' : '')}>
      <header className="osA-card-head">
        <span className="osA-card-tag">{tag}</span>
        <span className={'osA-card-status osA-card-status--' + status}>
          <span className="osA-card-dot" />
          {status}
        </span>
      </header>
      <h3 className="osA-card-name">{name}</h3>
      <p className="osA-card-desc">{desc}</p>
      <div className="osA-card-stack">
        {stack.map((s) => <span key={s}>{s}</span>)}
      </div>
      <footer className="osA-card-foot">
        <span className="osA-card-meta">{license}</span>
        <a className="osA-card-link" href={href} target="_blank" rel="noopener noreferrer">View on GitHub →</a>
      </footer>
    </article>
  );
}

export function OpenSourceA({ onNav }) {
  return (
    <div className="pageA">
      <SEO
        title="Open Source — Tools we use, given back | Aakeeo"
        description="Production Docker images, MCP server, Helm charts, CLI tooling, and Labs experiments from the Aakeeo team — maintained in public, MIT/AGPL licensed."
        keywords={['SuiteCRM Docker', 'SuiteCRM MCP server', 'open source CRM tools', 'Aakeeo open source']}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Open Source' },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Aakeeo Open Source',
          url: 'https://www.aakeeo.com/opensource',
          description: 'Open-source repositories maintained by Aakeeo — SuiteCRM Docker, MCP server, Tunnelr, and the LLM Wiki CRM.',
          publisher: {
            '@type': 'Organization',
            name: 'Aakeeo Technologies',
            logo: { '@type': 'ImageObject', url: 'https://www.aakeeo.com/logo.png' },
          },
          hasPart: [...CRM_PROJECTS, ...LABS_PROJECTS].map((p) => ({
            '@type': 'SoftwareSourceCode',
            name: p.name,
            codeRepository: `${GITHUB_ORG}/${p.repoPath}`,
            programmingLanguage: p.stack.join(', '),
            license: `https://spdx.org/licenses/${p.license}`,
            description: p.desc,
          })),
        }}
      />
      <NavBar page="opensource" onNav={onNav} />

      <main>

      {/* HERO */}
      <section className="osA-hero">
        <span className="eyebrowA">Open Source</span>
        <h1>
          The tools we use, <em>given&nbsp;back</em>.
        </h1>
        <p className="osA-hero-sub">
          Twelve years of shipping open-source CRMs means we&apos;ve written a lot of glue. We maintain the good parts in public — Docker images, MCP servers, Helm charts, and the occasional side project that deserved to escape the private repo.
        </p>
        <div className="osA-hero-stats">
          {CONTRIB_STATS.map((s) => (
            <div key={s.k}>
              <strong>{s.v}</strong>
              <span>{s.k}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CRM TOOLING */}
      <section className="osA-section">
        <div className="osA-section-head">
          <span className="eyebrowA">01 — CRM tooling</span>
          <h2>Everything you need to run SuiteCRM in&nbsp;production.</h2>
          <p>If you&apos;d rather self-host than hire us, start here. Same images, same patterns, same CLI we use on every client engagement.</p>
        </div>
        <div className="osA-grid">
          {CRM_PROJECTS.map((p) => (
            <ProjectCard key={p.name} {...p} />
          ))}
        </div>
      </section>

      {/* LABS */}
      <section className="osA-section osA-section--alt">
        <div className="osA-section-head">
          <span className="eyebrowA">02 — Labs</span>
          <h2>Experiments from the same&nbsp;engineers.</h2>
          <p>Side projects that outgrew the private repo. Not CRM — but built in the same spirit: open, self-hostable, no data-harvesting tier.</p>
        </div>
        <div className="osA-grid">
          {LABS_PROJECTS.map((p) => (
            <ProjectCard key={p.name} {...p} />
          ))}
        </div>
      </section>

      {/* CONTRIBUTE */}
      <section className="osA-contrib">
        <div className="osA-contrib-copy">
          <span className="eyebrowA">03 — Contribute</span>
          <h2>PRs welcome. Paid support&nbsp;available.</h2>
          <p>Every project above takes issues and PRs in the open. If you&apos;re using one in production and need faster response, we offer tiered support — community, priority, and dedicated — same team as our CRM engagements.</p>
          <div className="osA-contrib-cta">
            <button className="btnA-primary" onClick={() => onNav('contact')}>Ask about paid support</button>
            <a className="btnA-ghost" href={GITHUB_ORG} target="_blank" rel="noopener noreferrer">Browse on GitHub →</a>
          </div>
        </div>
        <div className="osA-contrib-tiers">
          <div className="osA-tier">
            <span className="osA-tier-label">Community</span>
            <strong>Free</strong>
            <p>GitHub issues. Best-effort response. Public forum.</p>
          </div>
          <div className="osA-tier osA-tier--featured">
            <span className="osA-tier-label">Priority</span>
            <strong>On request</strong>
            <p>48-hour SLA. Private Slack channel. Upgrade help.</p>
          </div>
          <div className="osA-tier">
            <span className="osA-tier-label">Dedicated</span>
            <strong>Custom</strong>
            <p>Named engineer. 24h SLA. Roadmap influence.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ctaA">
        <div className="ctaA-inner">
          <h2>Use what we&apos;ve built. Or have us build for&nbsp;you.</h2>
          <p>The OSS is enough for most teams. If you&apos;d rather outsource the engineering, our Solutions page shows how we work.</p>
          <div className="ctaA-buttons">
            <button className="btnA-primary" onClick={() => onNav('solutions')}>See Solutions</button>
            <button className="btnA-ghost" onClick={() => onNav('contact')}>Talk to us →</button>
          </div>
        </div>
      </section>

      </main>

      <Footer onNav={onNav} />
    </div>
  );
}
